<template>
  <div class="cinema-content">
    <!-- ツールバー：戻る / 作品 / 地域 / 上映バージョン -->
    <div ref="toolbarRef" class="cinema-toolbar">
      <button v-if="!embedded" type="button" class="cinema-back" @click="$emit('back')">
        <i class="fas fa-arrow-left" aria-hidden="true" />
        <span class="cinema-back__text">戻る</span>
      </button>

      <div class="cinema-movie">
        <img
          v-if="movie?.imgUrl"
          class="cinema-movie__poster"
          :src="movie.imgUrl"
          :alt="movie?.name"
          @error="onPosterError"
        >
        <div class="cinema-movie__meta">
          <p class="cinema-movie__name" :title="movie?.name">{{ movie?.name || '-' }}</p>
          <p class="cinema-movie__sub">
            <span>{{ dateLabel }}</span>
            <span v-if="totalCount != null" class="cinema-movie__count">
              {{ totalCount.toLocaleString() }}劇場
            </span>
          </p>
        </div>
      </div>

      <div class="cinema-filters">
        <RegionMultiPicker
          ref="regionPickerRef"
          :area-ids="areaIds"
          :prefecture-ids="prefectureIds"
          :nearby="nearby"
          @confirm="onRegionConfirm"
        />
        <VersionFilter
          ref="versionFilterRef"
          :model-value="versionSelection"
          :options="versionOptions"
          @change="onVersionChange"
        />
      </div>
    </div>

    <!-- 一覧 -->
    <div class="cinema-table-wrap">
      <table class="cinema-table">
        <thead>
          <tr>
            <th rowspan="2" class="col-rank">#</th>
            <th rowspan="2" class="col-name">
              <span>劇場名</span>
              <!-- 行クリックで週次詳細が開くことは見た目から分からないため、作品表と同じ書式で明示する -->
              <span class="cinema-name-hint">（クリックで週次詳細へ）</span>
            </th>
            <th
              rowspan="2"
              class="col-box sortable"
              :class="sortClass('boxOffice')"
              :aria-sort="ariaSort('boxOffice')"
              @click="changeSort('boxOffice')"
            >
              <span>興行収入</span>
              <span class="cinema-sort" aria-hidden="true">
                <i class="cinema-sort__arrow cinema-sort__arrow--up" :class="{ 'is-active': isSortedBy('boxOffice', SORT_ORDER_ASC) }" />
                <i class="cinema-sort__arrow cinema-sort__arrow--down" :class="{ 'is-active': isSortedBy('boxOffice', SORT_ORDER_DESC) }" />
              </span>
            </th>
            <th
              v-for="group in METRIC_GROUPS"
              :key="group.key"
              colspan="4"
              class="col-group sortable"
              :class="sortClass(group.sortCode)"
              :aria-sort="ariaSort(group.sortCode)"
              @click="changeSort(group.sortCode)"
            >
              <span>{{ group.label }}</span>
              <span class="cinema-sort" aria-hidden="true">
                <i class="cinema-sort__arrow cinema-sort__arrow--up" :class="{ 'is-active': isSortedBy(group.sortCode, SORT_ORDER_ASC) }" />
                <i class="cinema-sort__arrow cinema-sort__arrow--down" :class="{ 'is-active': isSortedBy(group.sortCode, SORT_ORDER_DESC) }" />
              </span>
            </th>
          </tr>
          <tr class="cinema-table__subhead">
            <template v-for="group in METRIC_GROUPS" :key="`sub-${group.key}`">
              <th class="col-total">計</th>
              <th v-for="slot in TIME_SLOTS" :key="`${group.key}-${slot.key}`" class="col-slot">
                {{ slot.label }}
              </th>
            </template>
          </tr>
        </thead>

        <tbody v-if="showSkeleton">
          <tr v-for="idx in 10" :key="`sk-${idx}`" class="cinema-skeleton-row">
            <td v-for="cell in 19" :key="`sk-${idx}-${cell}`">
              <span class="skeleton-block" />
            </td>
          </tr>
        </tbody>

        <tbody v-else>
          <tr v-if="!cinemas.length && !loading">
            <td class="cinema-empty" colspan="19">
              <span class="cinema-state-inner cinema-state-inner--block">
                <template v-if="errorMessage">
                  <p class="cinema-empty__title">データを取得できませんでした</p>
                  <button type="button" class="cinema-retry" @click="reload">再読み込み</button>
                </template>
                <template v-else>
                  <p class="cinema-empty__title">条件に合う上映がありません</p>
                  <p class="cinema-empty__hint">日付・地域・上映バージョンの条件をご確認ください</p>
                </template>
              </span>
            </td>
          </tr>

          <tr
            v-for="item in cinemas"
            :key="item.cinemaId"
            class="cinema-row"
            :class="{ 'is-opened': openedCinemaId === item.cinemaId }"
            @click="$emit('open-cinema', item)"
          >
            <td class="col-rank">{{ item.rankNo }}</td>
            <td class="col-name">
              <span class="cinema-name">{{ item.cinemaName }}</span>
              <span class="cinema-pref">{{ item.prefectureName }}</span>
            </td>
            <td class="col-box" :title="item.boxOfficeRangeText">
              <span class="cinema-box__num">{{ item.boxOffice }}</span>
              <span class="cinema-box__unit">{{ item.boxOfficeUnit }}</span>
            </td>

            <template v-for="group in METRIC_GROUPS" :key="`${item.cinemaId}-${group.key}`">
              <td class="col-total">{{ cellText(item, group, 'total') }}</td>
              <td v-for="slot in TIME_SLOTS" :key="`${item.cinemaId}-${group.key}-${slot.key}`" class="col-slot">
                {{ cellText(item, group, slot.key) }}
              </td>
            </template>
          </tr>

          <!-- colspan の中身は .cinema-state-inner で包む：スマホでは表が 960px 幅で
               横スクロールするため、包まずに中央寄せすると文字が画面外に出てしまう -->
          <tr v-if="loadingMore">
            <td class="cinema-more cinema-more--loading" colspan="19">
              <span class="cinema-state-inner">
                <i class="fas fa-spinner fa-spin" aria-hidden="true" /> 読み込み中…
              </span>
            </td>
          </tr>
          <tr v-else-if="loadMoreError">
            <td class="cinema-more" colspan="19">
              <span class="cinema-state-inner">
                <button type="button" class="cinema-retry" @click="loadMore">再試行</button>
              </span>
            </td>
          </tr>
          <tr v-else-if="cinemas.length && !hasMore">
            <td class="cinema-more cinema-more--end" colspan="19">
              <span class="cinema-state-inner">すべて表示しました</span>
            </td>
          </tr>
          <!-- 次ページ読み込みトリガー：実際にスクロールする要素（このコンポーネント自身か
               親の詳細ドロワーか）を問わず検知できるよう IntersectionObserver で監視する。
               hasMore が false でも DOM は維持し、observer の監視対象を保つ -->
          <tr aria-hidden="true">
            <td ref="sentinelRef" class="cinema-sentinel" colspan="19" />
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue'
import dayjs from 'dayjs'
import {
  getCinemaRankFilterOptionsService,
  getCinemaRankService,
} from '@/api/dp/index.js'
import {
  SORT_OPTIONS,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC,
  TIME_SLOTS,
  VERSION_DIMENSIONS,
  formatCount,
  formatRate,
  mapCinemaRankItem,
  pickCinemaList,
  pickVersionOptions,
} from '@/utils/dashboard-cinema-rank.js'
import { buildDateParams, formatRangeLabel } from '@/utils/dashboard-date-range'
import RegionMultiPicker from './region-multi-picker.vue'
import VersionFilter from './version-filter.vue'

/** 4指標グループ。sortCode は DpCinemaRankSort の code と対応 */
const METRIC_GROUPS = [
  { key: 'scheduleCount', label: '上映回数', sortCode: 'scheduleCount', type: 'count' },
  { key: 'seatTotal', label: '座席数', sortCode: 'seatTotal', type: 'count' },
  { key: 'occupiedSeats', label: '入場者数', sortCode: 'occupiedSeats', type: 'count' },
  { key: 'occupancyRate', label: '充足率', sortCode: 'occupancyRate', type: 'rate' },
]

const props = defineProps({
  /** 選択中の作品（movie-content の mapMovieRankItem 済みモデル） */
  movie: {
    type: Object,
    default: null,
  },
  /** 業務日レンジの終点（単日モードでは選択日そのもの） */
  selectedDate: {
    type: Object,
    default: () => dayjs(),
  },
  /** 業務日レンジの起点。未指定なら selectedDate と同日＝単日モード */
  startDate: {
    type: Object,
    default: null,
  },
  /** 初期地域（作品ランキングから引き継ぐ）。以降は本コンポーネント内で多選択に拡張される */
  initialAreaIds: {
    type: Array,
    default: () => [],
  },
  initialPrefectureIds: {
    type: Array,
    default: () => [],
  },
  /** 初期の「近隣50km」モード。initialAreaIds/initialPrefectureIds とは排他 */
  initialNearby: {
    type: Boolean,
    default: false,
  },
  /** 週次詳細を開いている劇場ID（行のハイライト用） */
  openedCinemaId: {
    type: Number,
    default: null,
  },
  /** 作品詳細ドロワーに埋め込んで使う場合 true。戻るボタンを隠すだけで、他のロジックは変えない */
  embedded: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['back', 'open-cinema', 'filters-change'])

const PAGE_SIZE = 25
/** 下端からこの距離まで来たら次ページを取りに行く（IntersectionObserver の rootMargin に使用） */
const LOAD_MORE_THRESHOLD_PX = 240

/** スクロール位置リセット時、ツールバー（フィルタ群）ごと画面上端に見せるための参照 */
const toolbarRef = ref(null)
/** 一覧末尾の監視要素。実際にスクロールするのが親（詳細ドロワー）側の場合でも
 *  検知できるよう、要素固定のスクロールイベントではなく IntersectionObserver で監視する。
 *  読み込み中は骨格表示（別 tbody）に切り替わり要素が入れ替わるため、監視対象の
 *  付け替えが必要 */
const sentinelRef = ref(null)
let sentinelObserver = null
let observedSentinelEl = null
const regionPickerRef = ref(null)
const versionFilterRef = ref(null)

const cinemas = ref([])
const totalCount = ref(null)
const hasMore = ref(false)
const nextCursor = ref(null)
const loading = ref(false)
const loadingMore = ref(false)
const initialLoading = ref(true)
const errorMessage = ref('')
const loadMoreError = ref(false)

const areaIds = ref([...props.initialAreaIds])
const prefectureIds = ref([...props.initialPrefectureIds])
const nearby = ref(props.initialNearby)
const sortBy = ref('boxOffice')
/** 並び替え方向。ランキングの自然な期待は「多い順」なので既定は降順 */
const sortOrder = ref(SORT_ORDER_DESC)
const versionOptions = ref({})
const versionSelection = ref(emptyVersionSelection())

let abortController = null
/** 進行中リクエストの世代番号。古いレスポンスが新しい結果を上書きするのを防ぐ */
let fetchId = 0

/** レンジ起点（親が渡さなければ終点と同日＝単日モード） */
const rangeStart = computed(() => dayjs(props.startDate ?? props.selectedDate))
const dateLabel = computed(() => formatRangeLabel(rangeStart.value, props.selectedDate))

/**
 * バージョン絞り込み・並び替え・地域変更など、初回以外の再読み込み中フラグ。
 * ローカル環境などレスポンスが速すぎると true→false が1フレーム未満で終わり、
 * スピナーが回る前に消えて「反応していない」ように見えるため、最低表示時間を設ける。
 */
const MIN_RELOADING_DISPLAY_MS = 400
const rawReloading = computed(() => loading.value && !initialLoading.value)
const reloading = ref(false)
/** 初回読み込みと再読み込みの両方で、同じ骨格表示を出す */
const showSkeleton = computed(() => (initialLoading.value && loading.value) || reloading.value)
let reloadingShownAt = 0
let hideReloadingTimer = null

watch(rawReloading, (isReloading) => {
  if (isReloading) {
    if (hideReloadingTimer) {
      clearTimeout(hideReloadingTimer)
      hideReloadingTimer = null
    }
    reloadingShownAt = Date.now()
    reloading.value = true
    return
  }
  if (!reloading.value) return
  const remaining = MIN_RELOADING_DISPLAY_MS - (Date.now() - reloadingShownAt)
  if (remaining > 0) {
    hideReloadingTimer = setTimeout(() => {
      hideReloadingTimer = null
      reloading.value = false
    }, remaining)
  } else {
    reloading.value = false
  }
})

/**
 * showSkeleton が false に戻った（骨格表示 → 本来の tbody に切り替わった）タイミングで
 * センチネル監視を張り直す。reload() 完了直後にセンチネルを監視し直しても、最低表示時間
 * ぶん遅れて骨格表示が外れる（上の reloading watch）ため、そちらだけを起点にすると
 * まだ本来の tbody がマウントされておらず監視が張れないまま取り残される。
 */
watch(showSkeleton, (isSkeleton) => {
  if (!isSkeleton) void nextTick(observeSentinel)
})

function emptyVersionSelection() {
  const selection = {}
  VERSION_DIMENSIONS.forEach((dim) => {
    selection[dim.key] = []
  })
  return selection
}

function sortClass(code) {
  return { 'is-sorted': sortBy.value === code }
}

/** その列がその方向で並んでいるか（▲▼ のどちらをハイライトするかの判定） */
function isSortedBy(code, order) {
  return sortBy.value === code && sortOrder.value === order
}

/** スクリーンリーダー向けの並び順。並び替えに使っていない列は 'none' */
function ariaSort(code) {
  if (sortBy.value !== code) return 'none'
  return sortOrder.value === SORT_ORDER_ASC ? 'ascending' : 'descending'
}

function cellText(item, group, slotKey) {
  const value = item[group.key]?.[slotKey]
  return group.type === 'rate' ? formatRate(value) : formatCount(value)
}

function onPosterError(event) {
  event.target.style.display = 'none'
}

/** 現在の絞り込み条件（cursor 以外）をまとめる */
function buildParams() {
  return {
    movieId: props.movie?.movieId,
    // 単日は date、期間は startDate/endDate。作品ランキングと同じ口径でそろえる
    ...buildDateParams(rangeStart.value, props.selectedDate),
    areaIds: areaIds.value,
    prefectureIds: prefectureIds.value,
    // 近隣モードは中心点（所属劇場）をサーバー側が解決するので、フラグだけ送る
    nearby: nearby.value || undefined,
    ...versionSelection.value,
    sortBy: sortBy.value,
    sortOrder: sortOrder.value,
    limit: PAGE_SIZE,
  }
}

function cancelInFlight() {
  fetchId += 1
  if (abortController) {
    abortController.abort()
    abortController = null
  }
}

function isAbortError(error) {
  return error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED'
}

/**
 * 1ページ目から取り直す。
 * 絞り込み・並び替えが変わったら必ずここを通す（古いカーソルは即座に無効化する）。
 */
async function reload() {
  if (!props.movie?.movieId) return
  cancelInFlight()
  const currentFetchId = fetchId
  nextCursor.value = null
  hasMore.value = false
  loadMoreError.value = false
  errorMessage.value = ''
  loading.value = true

  try {
    abortController = new AbortController()
    const res = await getCinemaRankService(buildParams(), { signal: abortController.signal })
    abortController = null
    if (currentFetchId !== fetchId) return
    applyResponse(res, { append: false })
  } catch (error) {
    abortController = null
    if (currentFetchId !== fetchId || isAbortError(error)) return
    cinemas.value = []
    totalCount.value = null
    errorMessage.value = String(error?.message || error)
    console.error('[dashboard] getCinemaRankService failed', error)
  } finally {
    if (currentFetchId === fetchId) {
      loading.value = false
      initialLoading.value = false
    }
  }
}

/** 次ページを追記。カーソルが無い / 取得中 / 末尾に達している場合は何もしない */
async function loadMore() {
  if (!hasMore.value || !nextCursor.value || loading.value || loadingMore.value) return
  const currentFetchId = fetchId
  const cursor = nextCursor.value
  loadingMore.value = true
  loadMoreError.value = false

  try {
    const res = await getCinemaRankService({ ...buildParams(), cursor })
    if (currentFetchId !== fetchId) return
    applyResponse(res, { append: true })
  } catch (error) {
    if (currentFetchId !== fetchId || isAbortError(error)) return
    loadMoreError.value = true
    console.error('[dashboard] getCinemaRankService loadMore failed', error)
  } finally {
    if (currentFetchId === fetchId) loadingMore.value = false
  }
}

function applyResponse(res, { append }) {
  const list = pickCinemaList(res).map(mapCinemaRankItem)
  if (append) {
    // 同一カーソルの二重送信やデータ変動に備え、劇場IDで重複を除く
    const seen = new Set(cinemas.value.map((item) => item.cinemaId))
    cinemas.value = [...cinemas.value, ...list.filter((item) => !seen.has(item.cinemaId))]
  } else {
    cinemas.value = list
  }
  totalCount.value = res?.totalCount ?? null
  hasMore.value = Boolean(res?.hasMore)
  nextCursor.value = res?.nextCursor ?? null
}

async function loadVersionOptions() {
  if (!props.movie?.movieId) return
  try {
    const res = await getCinemaRankFilterOptionsService({
      movieId: props.movie.movieId,
      ...buildDateParams(rangeStart.value, props.selectedDate),
      areaIds: areaIds.value,
      prefectureIds: prefectureIds.value,
      nearby: nearby.value || undefined,
    })
    const nextOptions = pickVersionOptions(res)
    versionOptions.value = nextOptions
    dropInvalidVersionSelection(nextOptions)
  } catch (error) {
    console.error('[dashboard] getCinemaRankFilterOptionsService failed', error)
    versionOptions.value = {}
  }
}

/**
 * 日付や地域が変わって選択中のバージョン値に上映が無くなった場合、その次元を「すべて」に戻す。
 * 放置すると結果が常に空になり、ユーザーには理由が分からない。
 * <p>
 * 選択肢一覧はバックエンドが全量を返すようになったため、「一覧に載っているか」ではなく
 * 「available か」で判定する。載ってはいるがグレーの値を選んだままにはしない。
 */
function dropInvalidVersionSelection(nextOptions) {
  let changed = false
  const next = { ...versionSelection.value }
  VERSION_DIMENSIONS.forEach((dim) => {
    const selected = next[dim.key]
    if (!Array.isArray(selected) || !selected.length) return
    const options = nextOptions[dim.optionsKey] ?? []
    const hit = options.find((opt) => opt.versionId === String(selected[0]))
    if (!hit || hit.available === false) {
      next[dim.key] = []
      changed = true
    }
  })
  if (changed) versionSelection.value = next
}

function maybeLoadMore() {
  if (!hasMore.value || loading.value || loadingMore.value || loadMoreError.value) return
  void loadMore()
}

/** 一覧末尾のセンチネルを監視し、ビューポート（実スクロール元が cinema-table-wrap 自身か
 *  祖先の詳細ドロワーかを問わず）に入ったら次ページを読み込む。
 *  骨格表示との切り替えで要素が入れ替わるたびに監視対象を付け替える */
function observeSentinel() {
  const el = sentinelRef.value
  if (!el || el === observedSentinelEl) return
  if (!sentinelObserver) {
    sentinelObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) maybeLoadMore()
      },
      { rootMargin: `${LOAD_MORE_THRESHOLD_PX}px` },
    )
  }
  if (observedSentinelEl) sentinelObserver.unobserve(observedSentinelEl)
  sentinelObserver.observe(el)
  observedSentinelEl = el
}

/** 絞り込み・並び替えの変更後は必ず先頭にスクロールを戻す（順位が変わるため）。
 *  実際のスクロールはこのコンポーネント自身ではなく親の詳細ドロワー側が持つため、
 *  ツールバーを画面上端に寄せる形で代用する */
function scrollToTop() {
  toolbarRef.value?.scrollIntoView?.({ block: 'start' })
}

function onRegionConfirm({ areaIds: nextAreaIds, prefectureIds: nextPrefectureIds, nearby: nextNearby }) {
  areaIds.value = nextAreaIds
  prefectureIds.value = nextPrefectureIds
  nearby.value = Boolean(nextNearby)
  emit('filters-change', {
    areaIds: nextAreaIds,
    prefectureIds: nextPrefectureIds,
    nearby: nearby.value,
  })
  scrollToTop()
  void loadVersionOptions()
  void reload()
}

function onVersionChange(next) {
  versionSelection.value = next
  scrollToTop()
  void reload()
}

/**
 * 並び替えの切り替え。
 * 同じ列をもう一度押したら方向を反転、別の列に移ったら降順から始める
 * （ランキングで最初に見たいのは常に「多い方」なので）。
 */
function changeSort(code) {
  if (!SORT_OPTIONS.some((opt) => opt.code === code)) return
  if (sortBy.value === code) {
    sortOrder.value = sortOrder.value === SORT_ORDER_DESC ? SORT_ORDER_ASC : SORT_ORDER_DESC
  } else {
    sortBy.value = code
    sortOrder.value = SORT_ORDER_DESC
  }
  scrollToTop()
  void reload()
}

/** 週次詳細と条件を揃えるため、親に現在のバージョン条件を渡す */
function currentVersionParams() {
  return { ...versionSelection.value }
}

watch(
  () => [props.movie?.movieId, rangeStart.value.format('YYYY-MM-DD'),
    dayjs(props.selectedDate).format('YYYY-MM-DD')],
  () => {
    if (!props.movie?.movieId) return
    initialLoading.value = true
    scrollToTop()
    void loadVersionOptions()
    void reload()
  },
  { immediate: true },
)

onMounted(() => {
  void nextTick(observeSentinel)
})

onBeforeUnmount(() => {
  sentinelObserver?.disconnect()
  sentinelObserver = null
  observedSentinelEl = null
})

onUnmounted(() => {
  cancelInFlight()
  if (hideReloadingTimer) clearTimeout(hideReloadingTimer)
})

defineExpose({ currentVersionParams, reload })
</script>

<style lang="less" scoped>
.cinema-content {
  /* --mc-* テーマ変数は親 .dashboard-page で定義済み */
  display: flex;
  flex-direction: column;
  background: var(--mc-bg, #fff);
  border: 1px solid var(--mc-border, #e5e5e5);
  border-radius: var(--border-radius, 6px);
  box-shadow: var(--shadow-sm);
  /* 親（詳細ドロワー）側で縦スクロールをまとめて処理するため、縦方向は裁剪しない。
     横方向のみ hidden にして角丸のはみ出しを防ぐ（auto にすると縦もスクロール対象に
     なってしまうため、横は auto ではなく hidden を使う） */
  overflow-x: hidden;
}

/* ---------- ツールバー ---------- */
.cinema-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  flex-shrink: 0;
  padding: 10px 14px;
  border-bottom: 1px solid var(--mc-border, #e5e5e5);
  background: var(--mc-bg-soft, #fafafa);
}

.cinema-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid var(--mc-border, #e5e5e5);
  border-radius: 15px;
  background: var(--mc-bg, #fff);
  color: var(--mc-text, #000);
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: var(--mc-accent, #f9da49);
    background: var(--mc-row-hover, #f5f5f5);
  }
}

.cinema-movie {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.cinema-movie__poster {
  width: 28px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}

.cinema-movie__meta {
  min-width: 0;
}

.cinema-movie__name {
  margin: 0;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--mc-text, #000);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}

.cinema-movie__sub {
  display: flex;
  gap: 8px;
  margin: 2px 0 0;
  color: var(--mc-text-muted, #4a4a4a);
  font-size: 11px;
  line-height: 1.3;
}

.cinema-movie__count {
  font-weight: 600;
}

.cinema-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  min-width: 0;
}

/* 地区パネルの既定はトリガー中央寄せ（prod のヘッダー用）だが、ここでは
   ツールバー内でトリガーが端に寄るため、中央寄せだと左にはみ出して見切れる。
   master の .region-picker--mp と同じく左端合わせにする */
.cinema-filters :deep(.region-multi__panel) {
  left: 0;
  transform: none;
}

/* ---------- テーブル ---------- */
.cinema-table-wrap {
  /* 縦方向は自然な高さのまま親（詳細ドロワー）側のスクロールに任せ、
     ここでは横方向（ワイドテーブル）のスクロールのみ受け持つ */
  overflow-x: auto;
  scrollbar-width: thin;
}

.cinema-table {
  width: 100%;
  min-width: 1120px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
  color: var(--mc-text, #000);
}

.cinema-table thead th {
  position: sticky;
  z-index: 2;
  padding: 6px 8px;
  background: var(--mc-bg-soft, #fafafa);
  color: var(--mc-text-muted, #4a4a4a);
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  border-bottom: 1px solid var(--mc-border, #e5e5e5);
}

/* 1行目と2行目でそれぞれの高さぶん下げて sticky させる */
.cinema-table thead tr:first-child th {
  top: 0;
}

.cinema-table thead tr.cinema-table__subhead th {
  top: 30px;
  font-size: 10px;
  font-weight: 500;
}

.cinema-table thead th.sortable {
  cursor: pointer;
  user-select: none;

  &:hover {
    color: var(--mc-text, #000);
    background: var(--mc-row-hover, #f0f0f0);
  }
}

.cinema-table thead th.is-sorted {
  color: var(--mc-text, #000);
  background: var(--mc-row-selected, #fdf3c9);
}

/* ▲▼ を縦に重ねた並び替えインジケータ。現在の方向だけを濃く出し、
   もう一方は「押せばそちらに並び替わる」ことを示す薄い状態で常に見せる */
.cinema-sort {
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  margin-left: 4px;
  vertical-align: middle;
}

.cinema-sort__arrow {
  width: 0;
  height: 0;
  border-left: 3px solid transparent;
  border-right: 3px solid transparent;
  opacity: 0.25;
  transition: opacity 0.15s ease;

  /* PC・スマホともに、ヘッダーの文字色（グレー/黒）に関係なく常に赤で示す */
  &--up {
    border-bottom: 4px solid var(--mc-accent-strong, #a31d15);
  }

  &--down {
    border-top: 4px solid var(--mc-accent-strong, #a31d15);
  }

  &.is-active {
    opacity: 1;
  }
}

.cinema-table tbody td {
  padding: 7px 8px;
  text-align: right;
  white-space: nowrap;
  border-bottom: 1px solid var(--mc-border, #eee);
  font-variant-numeric: tabular-nums;
}

.cinema-row {
  background: var(--mc-row, #fff);
  cursor: pointer;
  transition: background 0.15s ease;

  &:nth-child(even) {
    background: var(--mc-row-alt, #fafafa);
  }

  &:hover {
    background: var(--mc-row-hover, #f5f5f5);
  }

  &.is-opened {
    background: var(--mc-row-selected, #fdf3c9);
  }
}

.col-rank {
  width: 44px;
  text-align: center !important;
  color: var(--mc-text-muted, #4a4a4a);
  font-weight: 700;
}

.col-name {
  width: 220px;
  text-align: left !important;
  white-space: normal !important;
}

/* 作品表の「（クリック ★ で右側表示）」と同じ書式のヒント */
.cinema-name-hint {
  margin-left: 6px;
  color: var(--mc-text-muted, #8a8a8a);
  font-size: 11px;
  font-weight: 400;
}

.cinema-name {
  display: block;
  font-weight: 600;
  line-height: 1.3;
}

.cinema-pref {
  display: block;
  color: var(--mc-text-muted, #8a8a8a);
  font-size: 10px;
  line-height: 1.3;
}

.col-box {
  width: 110px;
  color: var(--mc-accent-strong, #a31d15);
  font-weight: 700;
}

.cinema-box__unit {
  margin-left: 1px;
  font-size: 10px;
  font-weight: 500;
}

/* グループ境界を薄い縦線で示す（16列を目で追えるように） */
.col-group,
.col-total {
  border-left: 1px solid var(--mc-border, #e5e5e5);
}

.col-total {
  font-weight: 700;
}

.col-slot {
  color: var(--mc-text-muted, #6a6a6a);
}

/* ---------- 状態表示 ---------- */
.cinema-empty,
.cinema-more {
  padding: 24px 8px !important;
  text-align: center !important;
  color: var(--mc-text-muted, #8a8a8a);
}

.cinema-state-inner {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.cinema-state-inner--block {
  display: inline-block;
}

.cinema-empty__title {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
}

.cinema-empty__hint {
  margin: 0;
  font-size: 11px;
}

.cinema-more {
  padding: 14px 8px !important;
  font-size: 11px;
}

.cinema-more--end {
  opacity: 0.6;
}

/* 次ページ読み込み中の文言は realtime（--mc-accent-strong）と同じ色で目立たせる */
.cinema-more--loading {
  color: var(--mc-accent-strong, #a31d15);
}

/* 読み込み中の文言を「実際に見えている範囲」の中央に置く。
   .cinema-table は min-width が効いており（PC:1120px / スマホ:960px）、
   .cinema-table-wrap が横スクロールする。この状態で colspan セルに text-align:center を
   かけても「表全体の幅」を基準に中央寄せされてしまい、可視範囲の中央にはならない
   （特にスマホは画面幅 <<< 960px なので大きくずれる）。
   左端基準に戻したうえで sticky + left:50% で可視領域（scrollport）の中央に貼り付ける
   ＝ スマホ既存の `.cinema-state-inner { position: sticky; left: 12px }` と同じ手法の中央版。
   セレクタ特異性 (0,2,0) はスマホ側 `.cinema-state-inner` (0,1,0) より高いので、
   メディアクエリの記述順に関係なくこちらが優先される（空表示・全件表示の左寄せはそのまま） */
.cinema-more--loading {
  text-align: left !important;
}

.cinema-more--loading .cinema-state-inner {
  position: sticky;
  left: 50%;
  transform: translateX(-50%);
}

/* 次ページ読み込みトリガー用の不可視センチネル。表示上の高さは持たせない */
.cinema-sentinel {
  height: 1px;
  padding: 0 !important;
  border: none !important;
}

.cinema-retry {
  margin-top: 8px;
  height: 28px;
  padding: 0 16px;
  border: 1px solid var(--mc-accent, #f9da49);
  border-radius: 14px;
  background: transparent;
  color: var(--mc-text, #000);
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: var(--mc-row-selected, #fdf3c9);
  }
}

.cinema-skeleton-row td {
  padding: 9px 8px;
}

.skeleton-block {
  display: block;
  height: 12px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    var(--mc-row-alt, #f0f0f0) 25%,
    var(--mc-row-hover, #e8e8e8) 37%,
    var(--mc-row-alt, #f0f0f0) 63%
  );
  background-size: 400% 100%;
  animation: cinema-skeleton 1.4s ease infinite;
}

@keyframes cinema-skeleton {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

/* ---------- レスポンシブ ---------- */
@media (max-width: 992px) {
  .cinema-toolbar {
    gap: 8px;
    padding: 8px 10px;
  }

  .cinema-filters {
    /* 幅が足りなくなったら次の行に落として、作品名を潰さない */
    margin-left: 0;
    width: 100%;
    order: 3;
  }

  /* フィルタ群は order:3 で別行に落ちるため、1行目は「戻る」＋作品情報だけになる。
     作品名の幅を固定px で見積もらず、余った横幅いっぱいまで伸ばしてから省略記号にする */
  .cinema-movie {
    flex: 1;
    min-width: 0;
  }

  .cinema-movie__name {
    max-width: none;
    font-size: 13px;
  }
}

@media (max-width: 768px) {
  .cinema-back__text {
    display: none;
  }

  .cinema-back {
    padding: 0 10px;
  }

  /* スマホでは地域を上段、上映バージョン（4項目）を下段に積む。
     横並びだと地域トリガーの高さが version-filter の2行ぶんと揃わず間延びして見えるため */
  .cinema-filters {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  /* スマホは横スクロールで全16列を見せる。指標を削らないことを優先 */
  .cinema-table {
    min-width: 960px;
    font-size: 11px;
  }

  .col-name {
    position: sticky;
    left: 0;
    z-index: 1;
    width: 150px;
    background: inherit;
    box-shadow: 1px 0 0 var(--mc-border, #e5e5e5);
  }

  .cinema-table thead th.col-name {
    z-index: 3;
  }

  /* 固定幅の劇場名列に PC 版と横並びでヒントを入れると劇場名が潰れるので、
     スマホでは「劇場名」の下に折り返して全文を表示する */
  .cinema-name-hint {
    display: block;
    margin-left: 0;
    font-size: 9px;
  }

  /* 興行収入は「557.5万円」程度に収まるので、狭めて他の指標に幅を回す */
  .col-box {
    width: 82px;
  }

  /* 表は 960px 幅で横スクロールするため、colspan セルの中身を中央寄せすると
     画面外に出て「読み込み中」が見えない。sticky で画面左端に貼り付ける */
  .cinema-empty,
  .cinema-more {
    text-align: left !important;
  }

  .cinema-state-inner {
    position: sticky;
    left: 12px;
  }
}
</style>
